import { useModuleAccess } from "@/hooks/useModuleAccess";
import { getCertStatus, getTrainStatus } from "@/lib/compliance-status";
import { useState, useRef, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getQuoteOfTheDay } from "@/lib/daily-quote";
import {
  Users, FileText, ClipboardList, Briefcase,
  UserPlus, CheckCircle, Clock,
  ChevronRight, BarChart3, Gift, Star,
  ArrowRight, BookOpen, ShieldAlert, LayoutGrid, Check, X,
  CalendarPlus, UserPlus2, FileSignature, LayoutTemplate, ListChecks, LogOut,
  Plus, Sparkles, EyeOff, Eye, Trash2, ChevronDown, Send,
  ShieldCheck, Laptop, Stethoscope, GraduationCap, AlertTriangle,
  ShieldQuestion, Hourglass,
} from "lucide-react";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ActionCard } from "@/components/ai/ActionCard";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";

import {
  apiGetPendingLeaveCount,
  apiGetPendingDocumentsCount,
  apiGetWorkflowTaskCount,
  apiGetOnboardingStats,
  apiGetConcernOpenCount,
  apiGetLeaveRequests,
  apiApproveLeaveRequest,
  apiRejectLeaveRequest,
  apiGetRecruitmentPositions,
  apiGetRecruitmentApplications,
  apiGetHRDocuments,
  apiGetSurveys,
  apiGetAnalyticsHeadcount,
  apiGetCelebrations,
  apiGetUiPrefs,
  apiSendAssistantMessage,
  getTenantSlug,
  apiGetMedicalChecks,
  apiGetCertificationOverview,
  apiGetTrainingOverview,
  apiGetEquipment,
  apiGetEquipmentStock,
  apiGetPendingOwnerInstances,
  apiClaimWorkflowOwner,
} from "@/lib/api";

// ── Stat number with optional loading state ────────────────────────────────
function StatNumber({ value, label }: { value: number | undefined; label: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      {value === undefined
        ? <Skeleton className="h-8 w-12" />
        : <span className="text-3xl font-bold tabular-nums text-foreground leading-none">{value}</span>
      }
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

// ── Sara Dashboard Widget ──────────────────────────────────────────────────
type ChatMessage = { role: "user" | "assistant"; content: string; actions?: string[] };

function SaraDashboardWidget() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const SARA_SUGGESTIONS = [
    { icon: <Clock className="h-3 w-3 text-amber-500" />,      label: t("hrDashboard.sara.suggestions.pendingApprovals"),   prompt: t("hrDashboard.sara.suggestions.pendingApprovalsPrompt") },
    { icon: <Briefcase className="h-3 w-3 text-blue-500" />,   label: t("hrDashboard.sara.suggestions.recruitmentStatus"),  prompt: t("hrDashboard.sara.suggestions.recruitmentStatusPrompt") },
    { icon: <UserPlus className="h-3 w-3 text-emerald-500" />, label: t("hrDashboard.sara.suggestions.onboardingProgress"), prompt: t("hrDashboard.sara.suggestions.onboardingProgressPrompt") },
    { icon: <Users className="h-3 w-3 text-indigo-500" />,     label: t("hrDashboard.sara.suggestions.headcountSummary"),   prompt: t("hrDashboard.sara.suggestions.headcountSummaryPrompt") },
    { icon: <ShieldAlert className="h-3 w-3 text-red-500" />,  label: t("hrDashboard.sara.suggestions.openConcerns"),       prompt: t("hrDashboard.sara.suggestions.openConcernsPrompt") },
    { icon: <Sparkles className="h-3 w-3 text-violet-500" />,  label: t("hrDashboard.sara.suggestions.generatePlan"),       prompt: t("hrDashboard.sara.suggestions.generatePlanPrompt") },
  ];

  // Shared storage key — same as AIAssistantSheet and AIAssistant page so all
  // three entry points read/write the same conversation history.
  const storageKey = `besthr_${getTenantSlug()}_${user?.id ?? "anon"}_sara`;

  // Shared session ID — persisted so Sara backend has full context across surfaces.
  const [sessionId] = useState<string>(() => {
    try {
      const stored = localStorage.getItem(`${storageKey}_sid`);
      if (stored) return stored;
    } catch {}
    const id = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try { localStorage.setItem(`${storageKey}_sid`, id); } catch {}
    return id;
  });

  // Load messages from shared localStorage so history is continuous regardless
  // of which Sara surface (dashboard widget, sheet, full page) was used last.
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    try {
      const raw = localStorage.getItem(`${storageKey}_msgs`);
      if (raw) {
        const parsed = JSON.parse(raw) as Array<{ role: string; content: string; actionsTaken?: string[] }>;
        return parsed.map(m => ({
          role: m.role as "user" | "assistant",
          content: m.content,
          actions: m.actionsTaken,
        }));
      }
    } catch {}
    return [];
  });

  const [input, setInput]         = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef   = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Persist messages to shared localStorage key so Sheet / full page see them.
  useEffect(() => {
    try {
      const toSave = messages.map((m, i) => ({
        id: `d-${i}-${m.role}`,
        role: m.role,
        content: m.content,
        actionsTaken: m.actions,
        timestamp: new Date().toISOString(),
      }));
      localStorage.setItem(`${storageKey}_msgs`, JSON.stringify(toSave));
    } catch {}
  }, [messages, storageKey]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const send = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;
    setInput("");
    if (textareaRef.current) { textareaRef.current.style.height = "44px"; }
    const userMsg: ChatMessage = { role: "user", content: trimmed };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setIsLoading(true);
    try {
      const resp = await apiSendAssistantMessage(
        updated.map(m => ({ role: m.role, content: m.content })),
        sessionId,
      );
      setMessages(prev => [...prev, { role: "assistant", content: resp.message, actions: resp.actionsTaken }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: t("hrDashboard.sara.error", "Something went wrong. Please try again.") }]);
    } finally {
      setIsLoading(false);
    }
  }, [messages, isLoading, sessionId, t]);

  const hasMsgs = messages.length > 0;

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(`${storageKey}_collapsed`) !== "0";
    } catch {
      return true;
    }
  });

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(`${storageKey}_collapsed`, next ? "1" : "0"); } catch {}
      return next;
    });
  };

  return (
    <Card className="shadow-card overflow-hidden h-full flex flex-col">

      {/* ── Collapsible header: shown whenever messages exist, or when collapsed ── */}
      {(hasMsgs || collapsed) && (
        <div className="flex items-center justify-between px-5 py-3 border-b border-border/60">
          <button
            onClick={toggleCollapsed}
            className="flex items-center gap-2 min-w-0"
            aria-expanded={!collapsed}
          >
            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full overflow-hidden bg-white shadow-sm border border-border">
              <img src="/besthr.png" alt="Sara" className="h-5 w-5 object-contain" />
            </div>
            <span className="text-sm font-semibold text-foreground">{t("hrDashboard.sara.title", "Sara")}</span>
            <span className="text-xs text-muted-foreground hidden sm:inline">· {t("hrDashboard.sara.subtitle", "AI HR Companion")}</span>
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${collapsed ? "-rotate-90" : ""}`} />
          </button>
          {!collapsed && (
            <div className="flex items-center gap-3">
              {hasMsgs && (
                <button
                  onClick={() => {
                    setMessages([]);
                    try {
                      localStorage.removeItem(`${storageKey}_msgs`);
                      localStorage.removeItem(`${storageKey}_sid`);
                    } catch {}
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t("hrDashboard.sara.newChat", "New chat")}
                </button>
              )}
              <Link to="/ai-assistant" className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                {t("hrDashboard.sara.fullView", "Full view")} <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          )}
        </div>
      )}

      {/* ── Empty state: greeting + suggestion pills ─────────────── */}
      {!collapsed && !hasMsgs && (
        <div className="relative px-6 pt-8 pb-5 flex-1 flex flex-col items-center justify-center text-center">
          <button
            onClick={toggleCollapsed}
            aria-label={t("hrDashboard.sara.collapse", "Collapse")}
            className="absolute top-3 right-3 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
          <img src="/besthr.png" alt="BestHR" className="h-8 mb-4" />
          <h2 className="text-xl font-bold text-foreground tracking-tight">{t("hrDashboard.sara.greeting", "Hi, I'm Sara")}</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6 max-w-sm">
            {t("hrDashboard.sara.description", "Your AI HR companion. Ask me anything about your team, or pick a starting point below.")}
          </p>

          {/* Suggestion pills — two wrapping rows, small and unobtrusive */}
          <div className="flex flex-wrap justify-center gap-1.5 w-full">
            {SARA_SUGGESTIONS.map(s => (
              <button
                key={s.label}
                onClick={() => send(s.prompt)}
                disabled={isLoading}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-3 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted hover:border-violet-200 dark:hover:border-violet-800 transition-all disabled:opacity-50 whitespace-nowrap"
              >
                {s.icon}
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Chat messages ─────────────────────────────────────────── */}
      {hasMsgs && (
        <div ref={scrollRef} className="overflow-y-auto px-5 py-5 space-y-5 flex-1 min-h-[180px] max-h-[360px]">
          {messages.map((msg, i) => (
            <div key={i} className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}>
              {msg.role === "assistant" ? (
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full overflow-hidden bg-white shadow-sm border border-border">
                  <img src="/besthr.png" alt="Sara" className="h-6 w-6 object-contain" />
                </div>
              ) : (
                <EmployeeAvatar
                  avatarUrl={user?.avatarUrl}
                  avatarColor={user?.avatarColor ?? "from-purple-500 to-indigo-500"}
                  initials={user?.initials ?? "?"}
                  size="sm"
                  className="flex-shrink-0"
                />
              )}
              <div className="max-w-[80%] space-y-1">
                <div className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  msg.role === "user"
                    ? "rounded-tr-sm bg-primary text-primary-foreground"
                    : "rounded-tl-sm border bg-card text-foreground shadow-sm"
                }`}>
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
                {msg.role === "assistant" && msg.actions && msg.actions.length > 0 && (
                  <ActionCard actions={msg.actions} />
                )}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex gap-3">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full overflow-hidden bg-white shadow-sm border border-border">
                <img src="/besthr.png" alt="Sara" className="h-6 w-6 object-contain" />
              </div>
              <div className="rounded-2xl rounded-tl-sm border bg-card px-4 py-3 shadow-sm">
                <div className="flex items-center gap-1">
                  <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:0ms]" />
                  <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:150ms]" />
                  <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:300ms]" />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Input ─────────────────────────────────────────────────── */}
      {!collapsed && (
        <div className="px-4 pb-4 pt-3 border-t border-border/60">
          <div className="flex items-end gap-2 rounded-xl border border-border bg-muted/30 px-3 py-2 focus-within:ring-2 focus-within:ring-violet-500/20 focus-within:border-violet-300 dark:focus-within:border-violet-700 transition-all">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }}
              onInput={e => {
                const t = e.currentTarget;
                t.style.height = "28px";
                t.style.height = Math.min(t.scrollHeight, 120) + "px";
              }}
              placeholder={t("hrDashboard.sara.placeholder", "Ask Sara anything...")}
              disabled={isLoading}
              rows={1}
              className="flex-1 resize-none bg-transparent text-sm outline-none placeholder:text-muted-foreground/50 disabled:opacity-50 py-1"
              style={{ height: "28px", maxHeight: "120px" }}
            />
            <button
              onClick={() => send(input)}
              disabled={!input.trim() || isLoading}
              className="shrink-0 h-8 w-8 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center hover:from-violet-600 hover:to-indigo-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              <Send className="h-3.5 w-3.5 text-white" />
            </button>
          </div>
          {!hasMsgs && (
            <div className="text-center mt-2">
              <Link to="/ai-assistant" className="text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors">
                {t("hrDashboard.sara.openFull", "Open full Sara experience")}
              </Link>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ── HR To-Do Widget ────────────────────────────────────────────────────────
interface CustomTask { id: string; text: string; done: boolean; createdAt: string }
interface TodoState {
  date: string;
  completedSystemIds: string[];
  hideCompleted: boolean;
  customTasks: CustomTask[];
}

function HRTodoWidget({
  pendingLeaveCount,
  pendingDocsCount,
  pendingDocIds,
  pendingTasksCount,
  totalConcerns,
  newApplicationsCount,
  expiringDocsCount,
  expiringDocIds,
}: {
  pendingLeaveCount?: number;
  pendingDocsCount?: number;
  pendingDocIds?: number[];
  pendingTasksCount?: number;
  totalConcerns?: number;
  newApplicationsCount: number;
  expiringDocsCount: number;
  expiringDocIds?: number[];
}) {
  const { t } = useTranslation();
  const { canAccess } = useModuleAccess();
  const navigate = useNavigate();
  const today = new Date().toDateString();
  const storageKey = `hr_todo_${getTenantSlug()}`;

  const loadState = (): TodoState => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return { date: today, completedSystemIds: [], hideCompleted: false, customTasks: [] };
      const parsed: TodoState = JSON.parse(raw);
      if (parsed.date !== today) {
        return { date: today, completedSystemIds: [], hideCompleted: false, customTasks: parsed.customTasks ?? [] };
      }
      return parsed;
    } catch { return { date: today, completedSystemIds: [], hideCompleted: false, customTasks: [] }; }
  };

  const [state, setState] = useState<TodoState>(loadState);
  const [newTaskText, setNewTaskText] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const save = (next: TodoState) => {
    setState(next);
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch {}
  };

  const pendingDocHref  = (pendingDocIds && pendingDocIds.length === 1)  ? `/documents?openDocument=${pendingDocIds[0]}`  : "/documents";
  const expiringDocHref = (expiringDocIds && expiringDocIds.length === 1) ? `/documents?openDocument=${expiringDocIds[0]}` : "/documents";

  const systemTasks: { id: string; label: string; href: string; count: number; dotColor: string }[] = [
    { id: "leave_pending",    label: t("hrDashboard.todo.tasks.leavePending", { count: pendingLeaveCount ?? 0 }), href: "/team-leave?tab=approvals",          count: pendingLeaveCount ?? 0,  dotColor: "bg-amber-500"   },
    { id: "docs_pending",     label: t("hrDashboard.todo.tasks.docsPending", { count: pendingDocsCount ?? 0 }),        href: pendingDocHref,                       count: pendingDocsCount ?? 0,   dotColor: "bg-blue-500"    },
    { id: "tasks_pending",    label: t("hrDashboard.todo.tasks.tasksPending", { count: pendingTasksCount ?? 0 }),         href: "/my-tasks",                          count: pendingTasksCount ?? 0,  dotColor: "bg-violet-500"  },
    { id: "concerns_open",    label: t("hrDashboard.todo.tasks.concernsOpen", { count: totalConcerns ?? 0 }),                       href: "/hr-cases?filter=open",  count: totalConcerns ?? 0,      dotColor: "bg-red-500"     },
    { id: "applications_new", label: t("hrDashboard.todo.tasks.applicationsNew", { count: newApplicationsCount }),         href: "/recruitment",                       count: newApplicationsCount,    dotColor: "bg-emerald-500" },
    { id: "docs_expiring",    label: t("hrDashboard.todo.tasks.docsExpiring", { count: expiringDocsCount }),            href: expiringDocHref,                      count: expiringDocsCount,       dotColor: "bg-orange-500"  },
  ].filter(task => {
    const moduleKey: Record<string, string> = { leave_pending: "teamLeave", docs_pending: "hrDocuments", tasks_pending: "myTasks", concerns_open: "hrOpenCases", applications_new: "recruitment", docs_expiring: "hrDocuments" };
    return task.count > 0 && canAccess(moduleKey[task.id]);
  });

  const isSystemDone = (id: string) => state.completedSystemIds.includes(id);
  const toggleSystem = (id: string) => {
    const next = isSystemDone(id) ? state.completedSystemIds.filter(x => x !== id) : [...state.completedSystemIds, id];
    save({ ...state, completedSystemIds: next });
  };
  const toggleCustom = (id: string) => {
    save({ ...state, customTasks: state.customTasks.map(t => t.id === id ? { ...t, done: !t.done } : t) });
  };
  const deleteCustom = (id: string) => {
    save({ ...state, customTasks: state.customTasks.filter(t => t.id !== id) });
  };
  const addCustom = () => {
    const text = newTaskText.trim();
    if (!text) return;
    const task: CustomTask = { id: `ct_${Date.now()}`, text, done: false, createdAt: new Date().toISOString() };
    save({ ...state, customTasks: [...state.customTasks, task] });
    setNewTaskText("");
  };

  const allSystemVisible = state.hideCompleted ? systemTasks.filter(t => !isSystemDone(t.id)) : systemTasks;
  const allCustomVisible  = state.hideCompleted ? state.customTasks.filter(t => !t.done)       : state.customTasks;
  const completedCount = systemTasks.filter(t => isSystemDone(t.id)).length + state.customTasks.filter(t => t.done).length;
  const totalCount     = systemTasks.length + state.customTasks.length;

  return (
    <Card className="shadow-card h-full flex flex-col">
      <CardHeader className="pt-5 pb-3 px-5 flex-row items-center justify-between border-b space-y-0 shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground"><ClipboardList className="h-4 w-4" /></span>
          <CardTitle className="text-sm font-semibold">{t("hrDashboard.todo.title", "To-Do")}</CardTitle>
          {totalCount > 0 && (
            <span className="text-xs text-muted-foreground tabular-nums">{t("hrDashboard.todo.doneCount", { completed: completedCount, total: totalCount })}</span>
          )}
        </div>
        <button
          onClick={() => save({ ...state, hideCompleted: !state.hideCompleted })}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          title={state.hideCompleted ? t("hrDashboard.todo.showDone", "Show done") : t("hrDashboard.todo.hideDone", "Hide done")}
        >
          {state.hideCompleted ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
          {state.hideCompleted ? t("hrDashboard.todo.showDone", "Show done") : t("hrDashboard.todo.hideDone", "Hide done")}
        </button>
      </CardHeader>
      <CardContent className="p-0 flex-1 flex flex-col min-h-0">
        {allSystemVisible.length === 0 && allCustomVisible.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-2 py-8 text-center px-5">
            <CheckCircle className="h-8 w-8 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">{t("hrDashboard.todo.allCaughtUp", "All caught up! Great work.")}</p>
          </div>
        ) : (
          <div className="divide-y divide-border/60 flex-1 overflow-y-auto">
            {allSystemVisible.map(task => {
              const done = isSystemDone(task.id);
              return (
                <div key={task.id} className={`flex items-center gap-3 px-5 py-3 group hover:bg-muted/20 transition-colors ${done ? "opacity-50" : ""}`}>
                  <button
                    onClick={() => toggleSystem(task.id)}
                    className={`shrink-0 h-5 w-5 rounded border-2 flex items-center justify-center transition-colors ${done ? "bg-foreground/10 border-foreground/30" : "border-border hover:border-primary"}`}
                  >
                    {done && <Check className="h-3 w-3 text-foreground/60" />}
                  </button>
                  <button
                    onClick={() => navigate(task.href)}
                    className={`flex-1 text-left text-sm flex items-center gap-2 ${done ? "line-through text-muted-foreground" : "hover:text-primary transition-colors"}`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${task.dotColor}`} />
                    {task.label}
                    {!done && <ChevronRight className="h-3.5 w-3.5 ml-auto shrink-0 text-muted-foreground/50 group-hover:text-primary transition-colors" />}
                  </button>
                </div>
              );
            })}
            {allCustomVisible.map(task => (
              <div key={task.id} className={`flex items-center gap-3 px-5 py-3 group hover:bg-muted/20 transition-colors ${task.done ? "opacity-50" : ""}`}>
                <button
                  onClick={() => toggleCustom(task.id)}
                  className={`shrink-0 h-5 w-5 rounded border-2 flex items-center justify-center transition-colors ${task.done ? "bg-foreground/10 border-foreground/30" : "border-border hover:border-primary"}`}
                >
                  {task.done && <Check className="h-3 w-3 text-foreground/60" />}
                </button>
                <span className={`flex-1 text-sm ${task.done ? "line-through text-muted-foreground" : ""}`}>{task.text}</span>
                {!task.done && (
                  <button
                    onClick={() => navigate(`/ai-assistant?prompt=${encodeURIComponent(task.text)}&autoSend=1`)}
                    className="shrink-0 flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors opacity-0 group-hover:opacity-100"
                    title={t("hrDashboard.todo.askSara", "Ask Sara")}
                  >
                  <Sparkles className="h-3.5 w-3.5" /> {t("hrDashboard.sara.title", "Sara")}
                  </button>
                )}
                <button
                  onClick={() => deleteCustom(task.id)}
                  className="shrink-0 text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
                  title={t("hrDashboard.todo.delete", "Delete")}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 px-5 py-3 border-t border-border/60 shrink-0">
          <input
            ref={inputRef}
            value={newTaskText}
            onChange={e => setNewTaskText(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") addCustom(); }}
            placeholder={t("hrDashboard.todo.addTaskPlaceholder", "Add a task…")}
            className="flex-1 text-sm bg-transparent outline-none text-foreground placeholder:text-muted-foreground/50"
          />
          <button
            onClick={addCustom}
            disabled={!newTaskText.trim()}
            className="shrink-0 h-6 w-6 rounded-md bg-muted flex items-center justify-center hover:bg-muted/80 disabled:opacity-40 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Quick Actions Bar ──────────────────────────────────────────────────────
function QuickActionsBar() {
  const { t } = useTranslation();
  const { canAccess } = useModuleAccess();
  const [open, setOpen] = useState(true);

  const actions = [
    { label: t("hrDashboard.quickActions.postJob"),              icon: <Briefcase className="h-3.5 w-3.5" />,      moduleKey: "recruitment", href: "/recruitment?action=create" },
    { label: t("hrDashboard.quickActions.addEmployee"),          icon: <UserPlus2 className="h-3.5 w-3.5" />,      moduleKey: "organization", href: "/organization?tab=employees&action=create" },
    { label: t("hrDashboard.quickActions.documentTemplate"),     icon: <LayoutTemplate className="h-3.5 w-3.5" />, moduleKey: "hrDocuments", href: "/documents?tab=templates" },
    { label: t("hrDashboard.quickActions.corporateEvent"),       icon: <CalendarPlus className="h-3.5 w-3.5" />,   moduleKey: "corporateEvents", href: "/corporate-calendar?action=create" },
    { label: t("hrDashboard.quickActions.createSurvey"),         icon: <ListChecks className="h-3.5 w-3.5" />,     moduleKey: "surveys", href: "/surveys?action=create" },
    { label: t("hrDashboard.quickActions.startOnboarding"),      icon: <UserPlus className="h-3.5 w-3.5" />,       moduleKey: "onboarding", href: "/onboarding?action=create" },
    { label: t("hrDashboard.quickActions.requestDocumentSign"), icon: <FileSignature className="h-3.5 w-3.5" />,  moduleKey: "hrDocuments", href: "/documents?action=request-sign" },
    { label: t("hrDashboard.quickActions.startOffboarding"),     icon: <LogOut className="h-3.5 w-3.5" />,         moduleKey: "onboarding", href: "/offboarding" },
  ];

  return (
    <Card className="shadow-card overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-muted/30 transition-colors"
      >
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t("hrDashboard.quickActions.title", "Quick Actions")}</p>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`grid transition-all duration-200 ease-in-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <div className="px-4 pb-3 flex flex-wrap gap-2 justify-center border-t border-border/50">
            <div className="w-full" />
            {actions.filter(action => canAccess(action.moduleKey)).map(action => (
              <Link
                key={action.label}
                to={action.href}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-muted/30 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/70 hover:border-border/80 transition-all"
              >
                {action.icon}
                {action.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

// ── Collapsible widget section ─────────────────────────────────────────────
function CollapsibleSection({
  icon, title, summary, children,
}: {
  icon: React.ReactNode;
  title: string;
  summary?: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between mb-3 group px-1"
      >
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground/70">{icon}</span>
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider group-hover:text-foreground transition-colors">
            {title}
          </span>
          {summary && (
            <span className="text-xs text-muted-foreground/50">· {summary}</span>
          )}
        </div>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground/50 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div className={`grid transition-all duration-300 ease-in-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          {children}
        </div>
      </div>
    </div>
  );
}

// ── Widget header row ──────────────────────────────────────────────────────
function WidgetHeader({ icon, title, href, linkLabel }: {
  icon: React.ReactNode;
  title: string;
  href: string;
  linkLabel?: string;
}) {
  const { t } = useTranslation();
  return (
    <CardHeader className="pt-5 pb-4 px-5 flex-row items-center justify-between border-b space-y-0">
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">{icon}</span>
        <CardTitle className="text-sm font-semibold">{title}</CardTitle>
      </div>
      <Link to={href} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-0.5 transition-colors">
        {linkLabel ?? t("hrDashboard.widgets.viewAll", "View all")} <ChevronRight className="h-3 w-3" />
      </Link>
    </CardHeader>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function HRDashboard() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const navigate    = useNavigate();

  // Tenant module config — gates the queries below so we don't poll endpoints
  // the backend now 403s for disabled modules.
  const { canAccess: isModuleOn } = useModuleAccess();

  // Counts
  const { data: pendingLeave }    = useQuery({ queryKey: ["pending-leave-count"],  queryFn: apiGetPendingLeaveCount, enabled: isModuleOn("teamLeave") });
  const { data: pendingDocs }     = useQuery({ queryKey: ["pending-docs-count"],   queryFn: apiGetPendingDocumentsCount, enabled: isModuleOn("hrDocuments") });
  const { data: pendingTasks }    = useQuery({ queryKey: ["workflow-task-count"],  queryFn: apiGetWorkflowTaskCount, enabled: isModuleOn("workflows") });
  const { data: onboardingStats } = useQuery({ queryKey: ["onboarding-stats"],     queryFn: apiGetOnboardingStats, enabled: isModuleOn("onboarding") });
  const { data: concernCount }    = useQuery({ queryKey: ["concern-open-count"],   queryFn: apiGetConcernOpenCount, enabled: isModuleOn("hrOpenCases") });

  // Data
  const { data: pendingLeaveReqs = [], isLoading: loadingLeave } = useQuery({
    queryKey: ["leave-requests", "Pending"],
    queryFn: () => apiGetLeaveRequests({ status: "Pending" }),
    enabled: isModuleOn("teamLeave"),
  });
  const { data: positions    = [] } = useQuery({ queryKey: ["recruitment-positions"],    queryFn: apiGetRecruitmentPositions, enabled: isModuleOn("recruitment") });
  const { data: applications = [] } = useQuery({ queryKey: ["recruitment-applications"], queryFn: () => apiGetRecruitmentApplications(), enabled: isModuleOn("recruitment") });
  const { data: hrDocuments  = [] } = useQuery({ queryKey: ["hr-documents"],             queryFn: () => apiGetHRDocuments(), enabled: isModuleOn("hrDocuments") });
  const { data: surveys      = [] } = useQuery({ queryKey: ["surveys"],                  queryFn: apiGetSurveys, enabled: isModuleOn("surveys") });
  const { data: headcount }         = useQuery({ queryKey: ["analytics-headcount"],      queryFn: () => apiGetAnalyticsHeadcount(), enabled: isModuleOn("hrAnalytics") });
  const { data: celebrations = [] } = useQuery({ queryKey: ["celebrations"],             queryFn: apiGetCelebrations });

  // Compliance & Equipment
  const { data: medicalChecks, isLoading: medicalLoading }    = useQuery({ queryKey: ["medical-checks-dashboard"], queryFn: apiGetMedicalChecks, enabled: isModuleOn("medicalCheckups") });
  const { data: certOverview = [], isLoading: certLoading }   = useQuery({ queryKey: ["cert-overview-dash"],       queryFn: apiGetCertificationOverview, enabled: isModuleOn("certifications") });
  const { data: trainingOverview = [], isLoading: trainLoading } = useQuery({ queryKey: ["training-overview-dash"], queryFn: apiGetTrainingOverview, enabled: isModuleOn("certifications") });
  const { data: allEquipment = [], isLoading: equipLoading }  = useQuery({ queryKey: ["all-equipment-dash"],       queryFn: () => apiGetEquipment(), enabled: isModuleOn("equipmentManagement") });
  const { data: equipmentStock = [] }                         = useQuery({ queryKey: ["equipment-stock-dash"],     queryFn: apiGetEquipmentStock, enabled: isModuleOn("equipmentManagement") });

  // Workflow instances awaiting an HR owner
  const { data: pendingOwnerInstances = [], isLoading: pendingOwnerLoading } = useQuery({
    queryKey: ["workflow-pending-owner"],
    queryFn: apiGetPendingOwnerInstances,
    enabled: isModuleOn("workflows"),
  });

  // Widget visibility
  const { data: widgetPrefs = {} } = useQuery({
    queryKey: ["ui-prefs", getTenantSlug()],
    queryFn: apiGetUiPrefs,
    staleTime: Infinity,
  });
  const wv = (id: string) => {
    const owner: Record<string, string> = {
      hr_leave_requests: "teamLeave", hr_recruitment: "recruitment", hr_workflow_owners: "workflows",
      hr_onboarding: "onboarding", hr_headcount: "hrAnalytics", hr_documents: "hrDocuments",
      hr_surveys: "surveys", hr_medical: "medicalCheckups", hr_certifications: "certifications",
      hr_trainings: "certifications", hr_equipment: "equipmentManagement",
    };
    if (owner[id] && !isModuleOn(owner[id])) return false;
    const k = `widget_${id}_visible`;
    return !(k in widgetPrefs) || widgetPrefs[k] === "true";
  };

  // Leave mutations
  const approveMutation = useMutation({
    mutationFn: (id: number) => apiApproveLeaveRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leave-requests"] });
      queryClient.invalidateQueries({ queryKey: ["pending-leave-count"] });
      toast.success(t("hrDashboard.leave.approvedToast", "Leave approved"));
    },
    onError: () => toast.error(t("hrDashboard.leave.approveError", "Failed to approve")),
  });
  const rejectMutation = useMutation({
    mutationFn: (id: number) => apiRejectLeaveRequest(id, ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leave-requests"] });
      queryClient.invalidateQueries({ queryKey: ["pending-leave-count"] });
      toast.success(t("hrDashboard.leave.rejectedToast", "Leave rejected"));
    },
    onError: () => toast.error(t("hrDashboard.leave.rejectError", "Failed to reject")),
  });

  // Workflow owner claim mutation
  const claimOwnerMutation = useMutation({
    mutationFn: (instanceId: number) => apiClaimWorkflowOwner(instanceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflow-pending-owner"] });
      toast.success(t("hrDashboard.workflowOwners.claimedToast", "You're now the owner of this workflow."));
    },
    onError: () => toast.error(t("hrDashboard.workflowOwners.claimError", "Failed to claim ownership")),
  });

  // Derived values
  const openPositions = positions.filter(p => p.status === "open");
  const totalConcerns = concernCount
    ? (concernCount.newCount ?? 0) + (concernCount.inReviewCount ?? 0) + (concernCount.assignedCount ?? 0)
    : undefined;
  const applicationsByStage = ["new","screening","interview","offer","hired"].map(s => ({
    stage: s, count: applications.filter(a => a.status === s).length,
  }));
  const now     = new Date();
  const in30    = new Date(now.getTime() + 30 * 86400000);
  const pendingDocsList  = hrDocuments.filter(d => d.status === "Pending");
  const expiredDocsList  = hrDocuments.filter(d => d.gdprExpired);
  const expiringDocsList = hrDocuments.filter(d => {
    if (!d.gdprExpiry || d.gdprExpired) return false;
    const exp = new Date(d.gdprExpiry);
    return exp >= now && exp <= in30;
  });
  const activeSurveys  = surveys.filter(s => s.status === "active");
  const upcomingEvents = celebrations.filter(c => c.daysAway >= 0 && c.daysAway <= 30).slice(0, 10);

  // Compliance derived stats
  const certCompliant    = certOverview.filter(e => getCertStatus(e) === "compliant").length;
  const certIssues       = certOverview.filter(e => e.missingCerts.length > 0 || e.expiredCerts.length > 0).length;
  const certExpiring     = certOverview.filter(e => e.expiringCerts.length > 0 && e.missingCerts.length === 0 && e.expiredCerts.length === 0).length;
  const certIssuesList   = certOverview.filter(e => e.missingCerts.length > 0 || e.expiredCerts.length > 0 || e.expiringCerts.length > 0).slice(0, 4);

  const trainCompliant   = trainingOverview.filter(e => getTrainStatus(e) === "compliant").length;
  const trainIssues      = trainingOverview.filter(e => e.missingTrainings.length > 0 || e.expiredTrainings.length > 0).length;
  const trainExpiring    = trainingOverview.filter(e => e.expiringTrainings.length > 0 && e.missingTrainings.length === 0 && e.expiredTrainings.length === 0).length;
  const trainIssuesList  = trainingOverview.filter(e => e.missingTrainings.length > 0 || e.expiredTrainings.length > 0 || e.expiringTrainings.length > 0).slice(0, 4);

  const equipAssigned   = allEquipment.filter(e => e.employeeId !== null).length;
  const equipUnassigned = allEquipment.filter(e => e.employeeId === null).length;
  const equipExpiring   = allEquipment.filter(e => {
    if (!e.expiryDate) return false;
    const d = new Date(e.expiryDate);
    return d >= now && d <= in30;
  });
  const totalStockItems = equipmentStock.reduce((s, i) => s + i.totalQuantity, 0);

  const dateLocale = i18n.language === "mk" ? "mk-MK" : "en-GB";

  // Summaries for collapsible headers
  const approvalsCount = pendingLeaveReqs.length;
  const recruitSummary = `${openPositions.length} ${t("hrDashboard.summary.open", "open")} · ${applications.filter(a => a.status === "new").length} ${t("hrDashboard.summary.new", "new")}`;
  const documentsSummary = `${pendingDocsList.length} ${t("hrDashboard.summary.pending", "pending")} · ${expiringDocsList.length} ${t("hrDashboard.summary.expiring", "expiring")}`;
  const surveysSummary = activeSurveys.length > 0
    ? t("hrDashboard.summary.xActive", { count: activeSurveys.length, defaultValue: "{{count}} active" })
    : t("hrDashboard.summary.noneActive", "none active");
  const complianceSummary = [
    isModuleOn("medicalCheckups") && medicalChecks ? t("hrDashboard.summary.medicalIssues", { count: medicalChecks.stats.total - medicalChecks.stats.valid, defaultValue: "{{count}} medical issues" }) : null,
    isModuleOn("certifications") && certOverview.length  ? t("hrDashboard.summary.certIssues",     { count: certIssues,   defaultValue: "{{count}} cert issues" })     : null,
    isModuleOn("certifications") && trainingOverview.length ? t("hrDashboard.summary.trainingIssues", { count: trainIssues, defaultValue: "{{count}} training issues" }) : null,
    isModuleOn("equipmentManagement") ? t("hrDashboard.summary.itemsAssigned", { count: equipAssigned, defaultValue: "{{count}} items assigned" }) : null,
  ].filter(Boolean).join(" · ");

  return (
    <AppLayout>
    <div className="space-y-6 w-full">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          {(() => { const q = getQuoteOfTheDay(); return (
            <p className="text-xs text-muted-foreground/70 italic max-w-md">
              "{q.text}" <span className="not-italic">— {q.author}</span>
            </p>
          ); })()}
        </div>
        <div className="flex items-center gap-2" data-tour="dashboard-toggle">
          <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-muted border border-border">
            <span className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold bg-background shadow-sm text-foreground">
              <BarChart3 className="h-3.5 w-3.5" />
              {t("hrDashboard.title", "HR Dashboard")}
            </span>
            <button
              onClick={() => {
                try { localStorage.setItem("hr_dashboard_view", "employee"); } catch {}
                navigate("/");
              }}
              className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-background/60 transition-colors"
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              {t("dashboard.employeeDashboard", "Employee Dashboard")}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3" data-tour="hrd-kpis">
        {isModuleOn("hrAnalytics") && <Card><CardContent className="p-4"><StatNumber value={headcount?.total} label={t("hrDashboard.headcount.totalEmployees")} /></CardContent></Card>}
        {isModuleOn("teamLeave") && <Card><CardContent className="p-4"><StatNumber value={pendingLeave?.count} label={t("hrDashboard.widgets.leaveApprovals")} /></CardContent></Card>}
        {isModuleOn("hrAnalytics") && <Card><CardContent className="p-4"><StatNumber value={headcount?.onLeave} label={t("hrDashboard.headcount.onLeave")} /></CardContent></Card>}
        {isModuleOn("medicalCheckups") && <Card><CardContent className="p-4"><StatNumber value={medicalChecks ? medicalChecks.stats.total - medicalChecks.stats.valid : undefined} label={t("hrDashboard.medicalAttention")} /></CardContent></Card>}
      </div>

      {/* ── Sara + To-Do ───────────────────────────────────────────────────── */}
      <div data-tour="hrd-overview" className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {isModuleOn("aiAssistant") && <SaraDashboardWidget />}
        <HRTodoWidget
          pendingLeaveCount={pendingLeave?.count}
          pendingDocsCount={pendingDocs?.count}
          pendingDocIds={pendingDocsList.map(d => d.id)}
          pendingTasksCount={pendingTasks?.count}
          totalConcerns={totalConcerns}
          newApplicationsCount={applications.filter(a => a.status === "new").length}
          expiringDocsCount={expiringDocsList.length}
          expiringDocIds={expiringDocsList.map(d => d.id)}
        />
      </div>

      {/* ── Quick Actions ──────────────────────────────────────────────────── */}
      <div data-tour="hrd-quick-actions">
        <QuickActionsBar />
      </div>

      {/* ── Approvals & Hiring ─────────────────────────────────────────────── */}
      <div data-tour="hrd-approvals">
      <CollapsibleSection
        icon={<Clock className="h-4 w-4" />}
        title={t("hrDashboard.sections.approvalsAndHiring", "Approvals & Hiring")}
        summary={`${approvalsCount} ${t("hrDashboard.summary.pending", "pending")} · ${recruitSummary}`}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-1">

          {/* Pending Leave Approvals */}
          {wv("hr_leave_requests") && (
            <Card className="shadow-card flex flex-col">
              <WidgetHeader icon={<Clock className="h-4 w-4" />} title={t("hrDashboard.widgets.leaveApprovals", "Leave Approvals")} href="/team-leave" />
              <CardContent className="p-0 flex-1">
                {loadingLeave ? (
                  <div className="p-5 space-y-3">
                    {[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full rounded-lg" />)}
                  </div>
                ) : pendingLeaveReqs.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-10 text-center px-5">
                    <CheckCircle className="h-8 w-8 text-muted-foreground/30" />
                    <p className="text-sm text-muted-foreground">{t("hrDashboard.leave.noPendingRequests", "No pending requests")}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {pendingLeaveReqs.slice(0, 4).map(req => (
                      <div key={req.id} className="flex items-center gap-3 px-5 py-3.5 hover:bg-muted/20 transition-colors">
                        <div
                          className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                          style={{ backgroundColor: req.employeeAvatarColor ?? "#94a3b8" }}
                        >
                          {req.employeeInitials ?? req.employeeName?.slice(0, 2).toUpperCase() ?? "?"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate leading-snug">{req.employeeName}</p>
                          <p className="text-xs text-muted-foreground leading-snug">
                            {req.leaveTypeLabel ?? req.leaveType} · {req.workingDays}d ·{" "}
                            {new Date(req.startDate).toLocaleDateString(dateLocale, { day: "numeric", month: "short" })}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:bg-muted" title={t("teamsManagement.approve", "Approve")}
                            onClick={() => approveMutation.mutate(req.id)} disabled={approveMutation.isPending}>
                            <Check className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:bg-muted" title={t("teamsManagement.reject", "Reject")}
                            onClick={() => rejectMutation.mutate(req.id)} disabled={rejectMutation.isPending}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Recruitment Pipeline */}
          {wv("hr_recruitment") && (
            <Card className="shadow-card flex flex-col">
              <WidgetHeader icon={<Briefcase className="h-4 w-4" />} title={t("hrDashboard.widgets.recruitment", "Recruitment")} href="/recruitment" />
              <CardContent className="p-5 flex-1 flex flex-col gap-5">
                <div className="space-y-2">
                  {applicationsByStage.map(({ stage, count }) => {
                    const total = applications.length || 1;
                    const pct = Math.round((count / total) * 100);
                    return (
                      <div key={stage} className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">{t(`hrDashboard.recruitment.stages.${stage}`, stage.charAt(0).toUpperCase() + stage.slice(1))}</span>
                          <span className="font-semibold tabular-nums text-foreground">{count}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className="h-full rounded-full bg-foreground/30 transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                {openPositions.length > 0 && (
                  <div className="mt-auto pt-2 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-2">{t("hrDashboard.recruitment.openPositions", { count: openPositions.length, plural: openPositions.length !== 1 ? "s" : "" })}</p>
                    <div className="space-y-1">
                      {openPositions.slice(0, 3).map(pos => (
                        <Link key={pos.id} to="/recruitment" className="flex items-center justify-between py-1 text-sm hover:text-primary transition-colors group">
                          <span className="truncate font-medium">{pos.title}</span>
                          <span className="text-xs text-muted-foreground shrink-0 ml-2 group-hover:text-primary">{pos.applicationCount ?? 0}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Workflows Awaiting Owner */}
          {wv("hr_workflow_owners") && (
            <Card className="shadow-card flex flex-col">
              <WidgetHeader icon={<ShieldQuestion className="h-4 w-4" />} title={t("hrDashboard.widgets.workflowOwners", "Workflows Awaiting Owner")} href="/workflows?tab=instances" />
              <CardContent className="p-0 flex-1">
                {pendingOwnerLoading ? (
                  <div className="p-5 space-y-3">
                    {[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full rounded-lg" />)}
                  </div>
                ) : pendingOwnerInstances.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-10 text-center px-5">
                    <CheckCircle className="h-8 w-8 text-muted-foreground/30" />
                    <p className="text-sm text-muted-foreground">{t("hrDashboard.workflowOwners.noneWaiting", "No workflows are waiting for an owner")}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {pendingOwnerInstances.slice(0, 4).map(inst => (
                      <div key={inst.id} className="flex items-center gap-3 px-5 py-3.5 hover:bg-muted/20 transition-colors">
                        <Hourglass className="h-4 w-4 text-amber-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate leading-snug">{inst.workflow_name}</p>
                          <p className="text-xs text-muted-foreground leading-snug truncate">
                            {inst.initiated_by_name && `${inst.initiated_by_name} · `}
                            {inst.owner_pending_since && t("hrDashboard.workflowOwners.waitingSince", { date: new Date(inst.owner_pending_since).toLocaleDateString(dateLocale, { day: "numeric", month: "short" }), defaultValue: "Waiting since {{date}}" })}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs shrink-0"
                          onClick={() => claimOwnerMutation.mutate(inst.id)}
                          disabled={claimOwnerMutation.isPending}
                        >
                          {t("hrDashboard.workflowOwners.claim", "Claim")}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Onboarding */}
          {wv("hr_onboarding") && (
            <Card className="shadow-card flex flex-col">
              <WidgetHeader icon={<UserPlus className="h-4 w-4" />} title={t("hrDashboard.widgets.onboarding", "Onboarding")} href="/onboarding" />
              <CardContent className="p-5 flex-1 flex flex-col gap-5">
                {onboardingStats ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-lg bg-muted/50 p-4 text-center">
                        <p className="text-3xl font-bold tabular-nums leading-none">{onboardingStats.activeSessions}</p>
                        <p className="text-xs text-muted-foreground mt-1.5">{t("hrDashboard.onboarding.active", "Active")}</p>
                      </div>
                      <div className="rounded-lg bg-muted/50 p-4 text-center">
                        <p className="text-3xl font-bold tabular-nums leading-none">{Math.round(onboardingStats.completionRate ?? 0)}%</p>
                        <p className="text-xs text-muted-foreground mt-1.5">{t("hrDashboard.onboarding.completed", "Completed")}</p>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{t("hrDashboard.onboarding.averageProgress", "Average progress")}</span>
                        <span>{Math.round(onboardingStats.avgProgress ?? 0)}%</span>
                      </div>
                      <Progress value={onboardingStats.avgProgress ?? 0} className="h-1.5" />
                    </div>
                    {onboardingStats.recentSessions.length > 0 && (
                      <div className="space-y-2 pt-1 border-t border-border">
                        {onboardingStats.recentSessions.slice(0, 3).map(s => (
                          <div key={s.id} className="flex items-center gap-2 text-xs">
                            <span className="flex-1 text-muted-foreground truncate">{s.employeeName}</span>
                            <div className="w-14 h-1.5 rounded-full bg-muted overflow-hidden shrink-0">
                              <div className="h-full bg-foreground/30 rounded-full" style={{ width: `${s.progress ?? 0}%` }} />
                            </div>
                            <span className="text-muted-foreground w-7 text-right tabular-nums shrink-0">{s.progress ?? 0}%</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="space-y-3">
                    <Skeleton className="h-20 w-full rounded-lg" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-12 w-full" />
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </CollapsibleSection>
      </div>

      {/* ── People & Documents ─────────────────────────────────────────────── */}
      <CollapsibleSection
        icon={<Users className="h-4 w-4" />}
        title={t("hrDashboard.sections.peopleAndDocuments", "People & Documents")}
        summary={`${headcount?.total ?? "–"} ${t("hrDashboard.summary.employees", "employees")} · ${documentsSummary}${isModuleOn("surveys") ? ` · ${surveysSummary}` : ""}`}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-1">

          {/* Headcount */}
          {wv("hr_headcount") && (
            <Card className="shadow-card flex flex-col">
              <WidgetHeader icon={<Users className="h-4 w-4" />} title={t("hrDashboard.widgets.headcount", "Headcount")} href="/hr-analytics" linkLabel={t("hrDashboard.widgets.fullAnalytics", "Full analytics")} />
              <CardContent className="p-5 flex-1 flex flex-col gap-4">
                {headcount ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: t("hrDashboard.headcount.totalEmployees", "Total employees"), value: headcount.total },
                        { label: t("hrDashboard.headcount.active", "Active"),          value: headcount.active },
                        { label: t("hrDashboard.headcount.onLeave", "On leave"),        value: headcount.onLeave },
                        { label: t("hrDashboard.headcount.newThisMonth", "New this month"),  value: headcount.newHiresThisMonth },
                      ].map(({ label, value }) => (
                        <div key={label} className="rounded-lg bg-muted/50 p-4">
                          <p className="text-2xl font-bold tabular-nums leading-none">{value}</p>
                          <p className="text-xs text-muted-foreground mt-1.5">{label}</p>
                        </div>
                      ))}
                    </div>
                    {headcount.byDepartment && headcount.byDepartment.length > 0 && (
                      <div className="pt-2 border-t border-border space-y-2">
                        <p className="text-xs text-muted-foreground font-medium">{t("hrDashboard.headcount.byDepartment", "By department")}</p>
                        {headcount.byDepartment.slice(0, 4).map(d => {
                          const pct = Math.round((d.count / (headcount.total || 1)) * 100);
                          return (
                            <div key={d.department} className="space-y-0.5">
                              <div className="flex justify-between text-xs">
                                <span className="text-muted-foreground truncate max-w-[120px]">{d.department}</span>
                                <span className="font-medium tabular-nums">{d.count}</span>
                              </div>
                              <div className="h-1 rounded-full bg-muted overflow-hidden">
                                <div className="h-full rounded-full bg-foreground/25" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <Skeleton className="h-20 rounded-lg" />
                      <Skeleton className="h-20 rounded-lg" />
                      <Skeleton className="h-20 rounded-lg" />
                      <Skeleton className="h-20 rounded-lg" />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Documents */}
          {wv("hr_documents") && (
            <Card className="shadow-card flex flex-col">
              <WidgetHeader icon={<FileText className="h-4 w-4" />} title={t("hrDashboard.widgets.documents", "Documents")} href="/documents" />
              <CardContent className="p-5 flex-1 flex flex-col gap-4">
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: t("hrDashboard.documents.pending", "Pending"),  count: pendingDocsList.length },
                    { label: t("hrDashboard.documents.expiring", "Expiring"), count: expiringDocsList.length },
                    { label: t("hrDashboard.documents.expired", "Expired"),  count: expiredDocsList.length },
                  ].map(({ label, count }) => (
                    <div key={label} className="rounded-lg bg-muted/50 p-3 text-center">
                      <p className="text-2xl font-bold tabular-nums leading-none">{count}</p>
                      <p className="text-xs text-muted-foreground mt-1.5">{label}</p>
                    </div>
                  ))}
                </div>
                <div className="flex-1 space-y-1 pt-1 border-t border-border">
                  {[...pendingDocsList, ...expiringDocsList].slice(0, 4).length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">{t("hrDashboard.documents.allUpToDate", "All documents up to date")}</p>
                  ) : (
                    [...pendingDocsList, ...expiringDocsList].slice(0, 4).map(doc => (
                      <div key={doc.id} className="flex items-center justify-between py-2 text-xs hover:bg-muted/30 rounded-lg px-2 -mx-2 transition-colors">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{doc.documentName}</p>
                          <p className="text-muted-foreground truncate">{doc.employeeName}</p>
                        </div>
                        <Badge variant="outline" className="text-[10px] shrink-0 ml-2">{t(`hrDashboard.documents.${doc.status.toLowerCase()}`, doc.status)}</Badge>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Surveys */}
          {wv("hr_surveys") && (
            <Card className="shadow-card flex flex-col">
              <WidgetHeader icon={<BookOpen className="h-4 w-4" />} title={t("hrDashboard.widgets.surveys", "Surveys")} href="/surveys" />
              <CardContent className="p-5 flex-1 flex flex-col gap-1">
                {activeSurveys.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-10 text-center">
                    <BookOpen className="h-8 w-8 text-muted-foreground/30" />
                    <p className="text-sm text-muted-foreground">{t("hrDashboard.surveys.noActiveSurveys", "No active surveys")}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activeSurveys.slice(0, 4).map(survey => {
                      const totalEmp = survey.totalEmployees ?? 0;
                      const rate = totalEmp > 0
                        ? Math.round((survey.responseCount / totalEmp) * 100) : 0;
                      return (
                        <div key={survey.id} className="space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-medium leading-snug">{survey.title}</p>
                            <span className="text-xs text-muted-foreground tabular-nums shrink-0">{rate}%</span>
                          </div>
                          <Progress value={rate} className="h-1.5" />
                          <p className="text-xs text-muted-foreground">
                            {t("hrDashboard.surveys.responded", { count: survey.responseCount, total: survey.totalEmployees })}
                            {survey.endDate && ` · ${t("hrDashboard.surveys.ends", "ends")} ${new Date(survey.endDate).toLocaleDateString(dateLocale, { day: "numeric", month: "short" })}`}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </CollapsibleSection>

      {/* ── People Events ──────────────────────────────────────────────────── */}
      {wv("hr_celebrations") && upcomingEvents.length > 0 && (
        <CollapsibleSection
          icon={<Gift className="h-4 w-4" />}
          title={t("hrDashboard.sections.peopleEvents", "People Events")}
          summary={`${upcomingEvents.length} ${t("hrDashboard.sections.inDays", "in the next 30 days")}`}
        >
          <Card className="shadow-card mt-1">
            <CardContent className="px-5 py-4">
              <div className="flex gap-3 overflow-x-auto pb-1">
                {upcomingEvents.map(c => (
                  <div
                    key={c.id}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg bg-muted/40 border border-border shrink-0 hover:bg-muted/60 transition-colors"
                  >
                    <div
                      className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                      style={{ backgroundColor: c.avatarColor ?? "#94a3b8" }}
                    >
                      {c.initials ?? c.name?.slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium leading-snug">{c.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {c.eventType === "birthday" ? t("hrDashboard.celebrations.birthday", "Birthday") : `${c.years}${t("hrDashboard.celebrations.anniversary", "yr anniversary")}`}
                        {" · "}
                        {c.daysAway === 0 ? t("hrDashboard.celebrations.today", "Today") : c.daysAway === 1 ? t("hrDashboard.celebrations.tomorrow", "Tomorrow") : t("hrDashboard.celebrations.inDays", { count: c.daysAway, defaultValue: "in {{count}} days" })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </CollapsibleSection>
      )}

      {/* ── Compliance & Equipment ─────────────────────────────────────────── */}
      <CollapsibleSection
        icon={<ShieldCheck className="h-4 w-4" />}
        title={t("hrDashboard.sections.complianceAndEquipment", "Compliance & Equipment")}
        summary={complianceSummary}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 pt-1">

          {/* Medical Checkups */}
          {wv("hr_medical") && (
            <Card className="shadow-card flex flex-col">
              <WidgetHeader icon={<Stethoscope className="h-4 w-4" />} title={t("hrDashboard.widgets.medicalCheckups", "Medical Checkups")} href="/medical-checkups" />
              <CardContent className="p-5 flex-1 flex flex-col gap-4">
                {medicalLoading ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2"><Skeleton className="h-16 rounded-lg" /><Skeleton className="h-16 rounded-lg" /></div>
                    <Skeleton className="h-20 rounded-lg" />
                  </div>
                ) : medicalChecks ? (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { label: t("medicalCheckups.stats.valid"),        value: medicalChecks.stats.valid,    color: "text-emerald-600" },
                        { label: t("medicalCheckups.stats.dueSoon"),      value: medicalChecks.stats.due_soon, color: medicalChecks.stats.due_soon > 0 ? "text-amber-600" : "" },
                        { label: t("medicalCheckups.stats.expired"),      value: medicalChecks.stats.expired,  color: medicalChecks.stats.expired > 0 ? "text-red-600" : "" },
                        { label: t("medicalCheckups.stats.failed"),       value: medicalChecks.stats.failed,   color: medicalChecks.stats.failed > 0 ? "text-rose-600" : "" },
                        { label: t("medicalCheckups.stats.noExam"),       value: medicalChecks.stats.no_exam,  color: medicalChecks.stats.no_exam > 0 ? "text-slate-600" : "" },
                        { label: t("medicalCheckups.stats.totalTracked"), value: medicalChecks.stats.total,    color: "" },
                      ].map(({ label, value, color }) => (
                        <div key={label} className="rounded-lg bg-muted/50 p-3 text-center">
                          <p className={`text-2xl font-bold tabular-nums leading-none ${color || "text-foreground"}`}>{value}</p>
                          <p className="text-xs text-muted-foreground mt-1.5">{label}</p>
                        </div>
                      ))}
                    </div>
                    {medicalChecks.employees.filter(e => e.status !== "valid").length > 0 && (
                      <div className="space-y-1.5 pt-1 border-t border-border">
                        <p className="text-xs text-muted-foreground font-medium">{t("hrDashboard.medical.needsAttention", "Needs attention")}</p>
                        {medicalChecks.employees
                          .filter(e => e.status !== "valid")
                          .slice(0, 3)
                          .map(emp => (
                            <div key={emp.employeeId} className="flex items-center justify-between py-1 text-xs">
                              <span className="font-medium truncate max-w-[120px]">{emp.employeeName}</span>
                              <span className={`shrink-0 ml-1 font-medium ${emp.status === "expired" ? "text-red-600" : emp.status === "due_soon" ? "text-amber-600" : emp.status === "failed" ? "text-rose-600" : "text-slate-600"}`}>
                                {t(`medicalCheckups.status.${emp.status}`)}
                              </span>
                            </div>
                          ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center gap-2 py-8 text-center">
                    <Stethoscope className="h-8 w-8 text-muted-foreground/30" />
                    <p className="text-sm text-muted-foreground">{t("hrDashboard.medical.noData", "No medical check data")}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Certifications */}
          {wv("hr_certifications") && (
            <Card className="shadow-card flex flex-col">
              <WidgetHeader icon={<ShieldCheck className="h-4 w-4" />} title={t("hrDashboard.widgets.certifications", "Certifications")} href="/certifications" />
              <CardContent className="p-5 flex-1 flex flex-col gap-4">
                {certLoading ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2"><Skeleton className="h-14 rounded-lg" /><Skeleton className="h-14 rounded-lg" /><Skeleton className="h-14 rounded-lg" /></div>
                    <Skeleton className="h-24 rounded-lg" />
                  </div>
                ) : certOverview.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-8 text-center">
                    <ShieldCheck className="h-8 w-8 text-muted-foreground/30" />
                    <p className="text-sm text-muted-foreground">{t("hrDashboard.certs.noPositions", "No positions configured yet")}</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="rounded-lg bg-muted/50 p-3 text-center">
                        <p className={`text-2xl font-bold tabular-nums leading-none ${certCompliant > 0 ? "text-emerald-600" : "text-foreground"}`}>{certCompliant}</p>
                        <p className="text-xs text-muted-foreground mt-1.5">{t("hrDashboard.certs.compliant", "Compliant")}</p>
                      </div>
                      <div className="rounded-lg bg-muted/50 p-3 text-center">
                        <p className={`text-2xl font-bold tabular-nums leading-none ${certExpiring > 0 ? "text-amber-600" : "text-foreground"}`}>{certExpiring}</p>
                        <p className="text-xs text-muted-foreground mt-1.5">{t("hrDashboard.certs.expiring", "Expiring")}</p>
                      </div>
                      <div className="rounded-lg bg-muted/50 p-3 text-center">
                        <p className={`text-2xl font-bold tabular-nums leading-none ${certIssues > 0 ? "text-red-600" : "text-foreground"}`}>{certIssues}</p>
                        <p className="text-xs text-muted-foreground mt-1.5">{t("hrDashboard.certs.issues", "Issues")}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {["noData", "notApplicable"].map(status => <span key={status}>{t(`certifications.status.${status}`)}: {certOverview.filter(employee => getCertStatus(employee) === status).length}</span>)}
                    </div>
                    {certIssuesList.length > 0 ? (
                      <div className="space-y-1.5 pt-1 border-t border-border">
                        <p className="text-xs text-muted-foreground font-medium">{t("hrDashboard.certs.topIssues", "Top issues")}</p>
                        {certIssuesList.map(emp => {
                          const issues = [...emp.expiredCerts.map(c => ({ title: c.title, type: "expired" as const })), ...emp.expiringCerts.map(c => ({ title: c.title, type: "expiring" as const })), ...emp.missingCerts.map(title => ({ title, type: "missing" as const }))];
                          const first = issues[0];
                          return (
                            <div key={emp.employeeId} className="flex items-start justify-between py-1 text-xs gap-1">
                              <span className="font-medium truncate">{emp.employeeName}</span>
                              {first && (
                                <span className={`shrink-0 font-medium ${first.type === "expired" ? "text-red-600" : first.type === "expiring" ? "text-amber-600" : "text-muted-foreground"}`}>
                                  {first.type === "missing" ? t("hrDashboard.certs.missing", "Missing") : first.type === "expired" ? t("hrDashboard.certs.expired", "Expired") : t("hrDashboard.certs.expiring", "Expiring")}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 pt-1 border-t border-border">
                        {certCompliant === certOverview.length ? <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" /> : <AlertTriangle className="h-4 w-4 text-muted-foreground shrink-0" />}
                        <p className="text-xs text-muted-foreground">{t(certCompliant === certOverview.length ? "hrDashboard.certs.allGood" : "hrDashboard.complianceIncomplete")}</p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Trainings */}
          {wv("hr_trainings") && (
            <Card className="shadow-card flex flex-col">
              <WidgetHeader icon={<GraduationCap className="h-4 w-4" />} title={t("hrDashboard.widgets.trainings", "Trainings")} href="/certifications" />
              <CardContent className="p-5 flex-1 flex flex-col gap-4">
                {trainLoading ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2"><Skeleton className="h-14 rounded-lg" /><Skeleton className="h-14 rounded-lg" /><Skeleton className="h-14 rounded-lg" /></div>
                    <Skeleton className="h-24 rounded-lg" />
                  </div>
                ) : trainingOverview.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-8 text-center">
                    <GraduationCap className="h-8 w-8 text-muted-foreground/30" />
                    <p className="text-sm text-muted-foreground">{t("hrDashboard.trainings.noPositions", "No training requirements configured yet")}</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="rounded-lg bg-muted/50 p-3 text-center">
                        <p className={`text-2xl font-bold tabular-nums leading-none ${trainCompliant > 0 ? "text-emerald-600" : "text-foreground"}`}>{trainCompliant}</p>
                        <p className="text-xs text-muted-foreground mt-1.5">{t("hrDashboard.trainings.compliant", "Compliant")}</p>
                      </div>
                      <div className="rounded-lg bg-muted/50 p-3 text-center">
                        <p className={`text-2xl font-bold tabular-nums leading-none ${trainExpiring > 0 ? "text-amber-600" : "text-foreground"}`}>{trainExpiring}</p>
                        <p className="text-xs text-muted-foreground mt-1.5">{t("hrDashboard.trainings.expiring", "Expiring")}</p>
                      </div>
                      <div className="rounded-lg bg-muted/50 p-3 text-center">
                        <p className={`text-2xl font-bold tabular-nums leading-none ${trainIssues > 0 ? "text-red-600" : "text-foreground"}`}>{trainIssues}</p>
                        <p className="text-xs text-muted-foreground mt-1.5">{t("hrDashboard.trainings.issues", "Issues")}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {["noData", "notApplicable"].map(status => <span key={status}>{t(`certifications.status.${status}`)}: {trainingOverview.filter(employee => getTrainStatus(employee) === status).length}</span>)}
                    </div>
                    {trainIssuesList.length > 0 ? (
                      <div className="space-y-1.5 pt-1 border-t border-border">
                        <p className="text-xs text-muted-foreground font-medium">{t("hrDashboard.trainings.topIssues", "Top issues")}</p>
                        {trainIssuesList.map(emp => {
                          const issues = [...emp.expiredTrainings.map(c => ({ title: c.title, type: "expired" as const })), ...emp.expiringTrainings.map(c => ({ title: c.title, type: "expiring" as const })), ...emp.missingTrainings.map(title => ({ title, type: "missing" as const }))];
                          const first = issues[0];
                          return (
                            <div key={emp.employeeId} className="flex items-start justify-between py-1 text-xs gap-1">
                              <span className="font-medium truncate">{emp.employeeName}</span>
                              {first && (
                                <span className={`shrink-0 font-medium ${first.type === "expired" ? "text-red-600" : first.type === "expiring" ? "text-amber-600" : "text-muted-foreground"}`}>
                                  {first.type === "missing" ? t("hrDashboard.trainings.missing", "Missing") : first.type === "expired" ? t("hrDashboard.trainings.expired", "Expired") : t("hrDashboard.trainings.expiring", "Expiring")}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 pt-1 border-t border-border">
                        {trainCompliant === trainingOverview.length ? <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" /> : <AlertTriangle className="h-4 w-4 text-muted-foreground shrink-0" />}
                        <p className="text-xs text-muted-foreground">{t(trainCompliant === trainingOverview.length ? "hrDashboard.trainings.allGood" : "hrDashboard.complianceIncomplete")}</p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Equipment */}
          {wv("hr_equipment") && (
            <Card className="shadow-card flex flex-col">
              <WidgetHeader icon={<Laptop className="h-4 w-4" />} title={t("hrDashboard.widgets.equipment", "Equipment")} href="/equipment" />
              <CardContent className="p-5 flex-1 flex flex-col gap-4">
                {equipLoading ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2"><Skeleton className="h-14 rounded-lg" /><Skeleton className="h-14 rounded-lg" /><Skeleton className="h-14 rounded-lg" /></div>
                    <Skeleton className="h-20 rounded-lg" />
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="rounded-lg bg-muted/50 p-3 text-center">
                        <p className="text-2xl font-bold tabular-nums leading-none">{equipAssigned}</p>
                        <p className="text-xs text-muted-foreground mt-1.5">{t("hrDashboard.equipment.assigned", "Assigned")}</p>
                      </div>
                      <div className="rounded-lg bg-muted/50 p-3 text-center">
                        <p className="text-2xl font-bold tabular-nums leading-none">{equipUnassigned}</p>
                        <p className="text-xs text-muted-foreground mt-1.5">{t("hrDashboard.equipment.unassigned", "Unassigned")}</p>
                      </div>
                      <div className="rounded-lg bg-muted/50 p-3 text-center">
                        <p className={`text-2xl font-bold tabular-nums leading-none ${equipExpiring.length > 0 ? "text-amber-600" : "text-foreground"}`}>{equipExpiring.length}</p>
                        <p className="text-xs text-muted-foreground mt-1.5">{t("hrDashboard.equipment.expiring", "Expiring")}</p>
                      </div>
                    </div>
                    {totalStockItems > 0 && (
                      <div className="flex items-center justify-between text-xs pt-1 border-t border-border">
                        <span className="text-muted-foreground">{t("hrDashboard.equipment.stockItems", "Stock items")}</span>
                        <span className="font-semibold tabular-nums">{totalStockItems}</span>
                      </div>
                    )}
                    {equipExpiring.length > 0 && (
                      <div className="space-y-1.5 border-t border-border pt-1">
                        <p className="text-xs text-muted-foreground font-medium">{t("hrDashboard.equipment.expiringSoon", "Expiring soon")}</p>
                        {equipExpiring.slice(0, 3).map(item => (
                          <div key={item.id} className="flex items-center justify-between py-1 text-xs">
                            <div className="min-w-0">
                              <p className="font-medium truncate">{item.itemName}</p>
                              {item.employeeName && <p className="text-muted-foreground truncate">{item.employeeName}</p>}
                            </div>
                            <span className="shrink-0 ml-2 font-medium text-amber-600">
                              {new Date(item.expiryDate!).toLocaleDateString(dateLocale, { day: "numeric", month: "short" })}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {allEquipment.length === 0 && (
                      <div className="flex flex-col items-center gap-2 py-4 text-center">
                        <Laptop className="h-8 w-8 text-muted-foreground/30" />
                        <p className="text-sm text-muted-foreground">{t("hrDashboard.equipment.noData", "No equipment tracked yet")}</p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

        </div>
      </CollapsibleSection>

    </div>
    </AppLayout>
  );
}
